import { useTranslation } from 'react-i18next'
import MarkerIcon from '@mui/icons-material/LocationOnOutlined'
import ManIcon from '@mui/icons-material/PersonOutline'
import TruckIcon from '@mui/icons-material/LocalShippingOutlined'
import CalendarIcon from '@mui/icons-material/CalendarMonth'

import { useTranslatedValue } from '../../../common/translation'
import { capitalizeFirstLetter } from '../../../utils/string'
import { mergeClassName } from '../../../utils/mergeClassName'
import {
  useGetQuoteConstraints,
  useGetQuoteMenAndTrucks,
  type Quote,
} from '../../../modules/quotes'
import { useQuoteContext } from '../QuoteComponent.context'
import Constraints from '../Constraints'
import Logo from './Logo'
import CompanyRatings from './CompanyRatings'
import QuotePrice from './QuotePrice'
import CheapestPrice from './CheapestPrice'
import { ignoreTimezone, useDateFormatter } from '../../../utils/date'

type ReactDiv = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

type HeaderProps = ReactDiv & {
  quote: Quote
}

const Header: React.FC<HeaderProps> = ({ quote, ...props }) => {
  const { t } = useTranslation()
  const formatDate = useDateFormatter()
  const {
    open,
    presentationMode,
    cardMode,
    ignoreConstraints = false,
    jobId,
  } = useQuoteContext()
  const { nbMen, nbTrucks } = useGetQuoteMenAndTrucks()(quote, jobId)
  const getTranslation = useTranslatedValue()
  const hasConstraints = useGetQuoteConstraints()(quote).length > 0
  const firstJob = quote.jobs[0]
  const hasPickupWindow = !!firstJob.dateRange?.pickupWindowEndDate
  const forAlternativeDate = !hasPickupWindow && firstJob.forAlternativeDate
    ? formatDate(ignoreTimezone(firstJob.date), {
      showYear: false,
      shortMonth: true,
      shortDay: true,
    })
    : undefined

  const small = !!open

  return (
    <div
      {...props}
      className={mergeClassName(
        'flex pr-2 items-center gap-x-2 flex-col-reverse md:flex-row',
        small && 'pr-0',
        !presentationMode && !cardMode && 'cursor-pointer',
        props.className,
      )}
    >
      <div className={mergeClassName(
        'flex grow flex-col sm:flex-row items-center gap-4 md:gap-x-8 md:pl-4 py-4 transition-all duration-200',
        small && 'gap-x-5 ',
      )}
      >
        { quote.companyBranch.logo?.en && (
          <div className={mergeClassName(
            'my-4 sm:my-0',
            small && 'mb-0',
            cardMode && 'my-1',
          )}
          >
            <Logo
              url={quote.companyBranch.logo.en}
              small={small}
            />
          </div>
        ) }

        <div className="grow">
          <div
            className={mergeClassName(
              'mb-2 hidden text-center font-body2 text-lg font-bold uppercase leading-5 sm:mb-1 sm:block sm:text-left sm:text-2xl sm:font-normal',
              (small || cardMode) && 'block',
            )}
          >
            { capitalizeFirstLetter(
              getTranslation(quote.companyBranch.name),
            ) }
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 sm:justify-start sm:px-0">

            <div className="flex items-center gap-1 text-sm text-neutral-400">
              <MarkerIcon className="!text-[20px]" />
              { capitalizeFirstLetter(quote.companyBranch.city) },
              { ' ' }
              { quote.companyBranch.region }
            </div>

            { !!nbMen && (
              <div className={mergeClassName(
                'hidden items-center gap-1 text-sm text-neutral-400 sm:flex',
                cardMode && 'flex',
              )}
              >
                <ManIcon className="!text-[18px]" />
                <div className="inline-block font-bold text-neutral-500">
                  { nbMen }
                </div>
              </div>
            ) }

            { nbTrucks && (
              <div className={mergeClassName(
                'hidden items-center gap-1 text-sm text-neutral-400 sm:flex',
                cardMode && 'flex',
              )}
              >
                <div>
                  { nbTrucks.map(({ key, value }) => (
                    <div key={key}>
                      <TruckIcon className="mr-2 !text-[18px]" />
                      <div className="mr-1 inline-block">
                        { value } x
                      </div>
                      <div className="inline-block font-bold text-neutral-500">
                        { t(`trucks.${key}.length`, { count: value }) }
                      </div>
                    </div>
                  )) }
                </div>
              </div>
            ) }

            { !presentationMode && (
              <div>
                <CompanyRatings branch={quote.companyBranch} />
              </div>
            ) }

            { forAlternativeDate && !ignoreConstraints && (
              <div className="flex items-center gap-1 text-sm text-yellow-700">
                <CalendarIcon className="!text-[20px]" />
                { forAlternativeDate }
              </div>
            ) }
          </div>
        </div>

      </div>

      <div className="shrink-0">
        <div className="flex items-center gap-4">

          <div className="ml-0 w-auto xs:ml-[-85px] xs:w-[85px]">
            { !cardMode && (
              <div className="flex justify-end">
                { hasConstraints
                  ? (
                    <Constraints quote={quote} compact />
                    )
                  : (
                    <CheapestPrice quote={quote} />
                    ) }
              </div>
            ) }
          </div>
          { !cardMode && (
            <QuotePrice quote={quote} />
          ) }
        </div>
      </div>
    </div>
  )
}

export default Header
